import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { KeyStatType, statButtons } from '../../constants'
import { formatPercent } from '../../../../../utils/utils'
import { black, grey } from '../../../../../colors'
import ReportsChip from '../../ReportsChip'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CustomTooltip from '../../../../../components/CustomTooltip'
import { type KeyStatSurveyItem, type ICalculation, type ReportsV2 } from '../../../../../models/Report'
import { useReportsContext } from '../../../ReportsContext'
import { SurveyQuestionType } from '../../../../../models/Survey'
import StatButtonGroup from '../StatButtonGroup'

interface TitleProps {
  titleText: string
  reports: ReportsV2
  surveyItem: KeyStatSurveyItem | null
  calculations: ICalculation[] | null
  selectedStat?: KeyStatType
  setSelectedStat?: (stat: KeyStatType) => void
}

const statTypes = [
  KeyStatType.AVERAGE,
  KeyStatType.TOP_2,
  KeyStatType.BOTTOM_2
]

const Title: React.FC<TitleProps> = ({ titleText, reports, surveyItem, selectedStat, setSelectedStat }) => {
  const { calculations, options } = surveyItem ?? {}
  const { selectedTrend } = useReportsContext()
  const shwoStatsButtons = selectedTrend > 2
  const { titles } = reports
  const showStats = calculations?.filter((keyStatistic) => statTypes.includes(keyStatistic.name as KeyStatType))
  const isLinearScaleQuestion = surveyItem?.itemType === SurveyQuestionType.LinearScale
  const theme = useTheme()
  const formatStatValue = (name: string, value: number | string): string | number => {
    return name === KeyStatType.AVERAGE
      ? value ?? 'N/A'
      : value ? formatPercent(value) : 'N/A'
  }

  const getTitle = (title: string, index: number): JSX.Element => (
    <Box display={'flex'}>
      <CustomTooltip title={'This comparison chart is not displayed because the minimum responses threshold was not met or no data was available for this survey item'}>
        <span>
          {title}
          <WarningAmberIcon sx={{ fontSize: '18px', ml: 0.5, color: grey[250] }} />
        </span>
      </CustomTooltip>
    </Box>
  )

  const DataChips: React.FC<{
    titles: string[]
    options: any[]
    getTitle: (title: string, index: number) => JSX.Element
  }> = ({ titles, options, getTitle }) => (
    <Box display={'flex'} flexWrap={'wrap'} gap={2}>
      {titles && titles.length > 1 && titles
        .map((title: string, index: number) => ({ title, index }))
        .filter(({ index }) => options?.[0]?.counts[index] === null)
        .map(({ title, index }) => (
          <ReportsChip
            key={index}
            index={-1}
            label={getTitle(title, index)}
          />
        ))}
    </Box>
  )

  const StatValues: React.FC<{
    showStats: ICalculation[]
    formatStatValue: (name: string, value: number | string) => string | number
  }> = ({ showStats, formatStatValue }) => (
    <Box display="flex" gap={5} flexWrap={'wrap'}>
      {showStats?.map(({ name, values }) => (
        <Box
          key={name}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography fontWeight={600} color={black[100]} whiteSpace={'nowrap'}>{name}:</Typography>
          {values?.map((value, index) => (
            <ReportsChip
              key={index}
              index={index}
              label={formatStatValue(name, value)}
            />
          ))}
        </Box>
      ))}
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, mb: 2 }} >
      <Box display={'flex'} flexDirection={'column'} gap={2.5}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant="h6" color={grey[700]} fontWeight="500">
            {titleText}
          </Typography>
          {shwoStatsButtons && isLinearScaleQuestion && (
            <StatButtonGroup
              buttons={statButtons}
              selected={selectedStat ?? KeyStatType.AVERAGE}
           onSelect={(value) => { setSelectedStat?.(value as KeyStatType) }}
            />
          )}
        </Box>
        <DataChips titles={titles} options={options ?? []} getTitle={getTitle} />
      </Box>
      {showStats && !shwoStatsButtons && (
        <StatValues showStats={showStats} formatStatValue={formatStatValue} />
      )}
    </Box>
  )
}

export default Title
